/**
 * Kurs-Landingpage — EINE Vorlage für alle Kurse.
 *
 * Route: /kurs/:slug   z. B. /kurs/kindergarten-kickboxen
 *
 * Die Seite kennt keinen einzigen Kurs. Name, Dauer und die nächsten freien
 * Termine kommen aus dem Termin-Kern, die Texte aus backend/kursseiten.py.
 *
 * Gestaltung (2. Fassung, nach Philips Feedback am ersten Entwurf):
 * durchgehend dunkel wie Seminar-Seite und Funnel — Grundfläche #0d0d10,
 * Akzent #ef7c23, Anton-Versalien, diagonales Akzentband im Kopf, vollbreite
 * Abschnitte statt Karten-Einerlei. Auch der Kinderkurs ist dunkel: Die
 * Referenzseite (Tanzschule, Teens) fährt dasselbe, und Anzeige, Funnel und
 * Landingpage sollen wie ein Stück wirken. Das `thema`-Feld der API bleibt
 * bestehen, falls das zurückgedreht werden soll.
 *
 * Bewegung: Abschnitte blenden beim Scrollen einmalig ein (gestaffelt),
 * das Kopfband zieht leicht mit (Parallax, nur transform → GPU). Muster wie
 * in FunnelSeite.jsx. `prefers-reduced-motion` schaltet alles ab.
 *
 * Gebucht wird hier NICHT. Der Knopf führt in den bestehenden Funnel, der
 * Einwilligung, Kapazität und die Übernahme als Lead schon beherrscht.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSeitenaufruf } from '@/hooks/useSeitenaufruf';

/* Termine kommen als UTC. Angezeigt wird FEST Europe/Berlin — der Kurs
   findet in Eutingen statt, egal wo der Leser gerade sitzt. */
const ORT = { timeZone: 'Europe/Berlin' };
function terminText(iso) {
  const d = new Date(iso);
  return {
    tag: d.toLocaleDateString('de-DE', { ...ORT, weekday: 'short' }).replace('.', ''),
    tagNr: d.toLocaleDateString('de-DE', { ...ORT, day: '2-digit' }),
    monat: d.toLocaleDateString('de-DE', { ...ORT, month: 'short' }).replace('.', ''),
    zeit: d.toLocaleTimeString('de-DE', { ...ORT, hour: '2-digit', minute: '2-digit' }),
    lang: d.toLocaleDateString('de-DE', { ...ORT, weekday: 'long' }),
  };
}

function rhythmus(termine) {
  const gesehen = new Map();
  termine.forEach((t) => {
    const { lang, zeit } = terminText(t.start);
    if (!gesehen.has(lang + zeit)) gesehen.set(lang + zeit, { lang, zeit });
  });
  const eintraege = [...gesehen.values()];
  if (!eintraege.length) return null;
  const zeiten = [...new Set(eintraege.map((e) => e.zeit))];
  const tage = [...new Set(eintraege.map((e) => e.lang))];
  const tagText = tage.length === 1 ? `${tage[0]}s`
    : tage.slice(0, -1).map((t) => `${t}s`).join(', ') + ` und ${tage.at(-1)}s`;
  return zeiten.length === 1 ? `${tagText} ${zeiten[0]} Uhr` : tagText;
}

/* ------------------------------------------------ Scroll-Animation (wie Funnel) */

function useSichtbar() {
  const ref = useRef(null);
  const [sichtbar, setSichtbar] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)) { setSichtbar(true); return; }
    const beobachter = new IntersectionObserver(([eintrag]) => {
      if (eintrag.isIntersecting) { setSichtbar(true); beobachter.disconnect(); }
    }, { rootMargin: '0px 0px -70px 0px', threshold: 0.08 });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);
  return [ref, sichtbar];
}

function Auf({ children, verz = 0, als: Als = 'div', className = '' }) {
  const [ref, sichtbar] = useSichtbar();
  return (
    <Als ref={ref} className={`ks-auf${sichtbar ? ' sichtbar' : ''} ${className}`}
         style={{ transitionDelay: `${verz}ms` }}>
      {children}
    </Als>
  );
}

/* Kopfband zieht beim Scrollen leicht mit — nur transform, läuft auf der GPU. */
function useParallax(staerke = 0.3) {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let laeuft = false;
    const zeichnen = () => {
      laeuft = false;
      const el = ref.current;
      if (el) el.style.transform = `translate3d(0, ${Math.min(window.scrollY, 600) * staerke}px, 0)`;
    };
    const beiScroll = () => { if (!laeuft) { laeuft = true; requestAnimationFrame(zeichnen); } };
    zeichnen();
    window.addEventListener('scroll', beiScroll, { passive: true });
    return () => window.removeEventListener('scroll', beiScroll);
  }, [staerke]);
  return ref;
}

/* ------------------------------------------------------------------ Seite */

export default function KursSeite() {
  const { slug } = useParams();
  useSeitenaufruf('kurs');

  const [kurs, setKurs] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [bildDa, setBildDa] = useState(true);
  const parallaxRef = useParallax();

  useEffect(() => {
    let abgebrochen = false;
    setKurs(null); setFehler(null); setBildDa(true);
    fetch(`/api/public/kurs/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (!abgebrochen) setKurs(d); })
      .catch((s) => { if (!abgebrochen) setFehler(s === 404 ? 'nicht_gefunden' : 'fehler'); });
    return () => { abgebrochen = true; };
  }, [slug]);

  if (fehler) {
    return (
      <div className="ks-wurzel"><Stil />
        <div className="ks-mitte">
          <h1 className="ks-h1" style={{ fontSize: 34 }}>
            {fehler === 'nicht_gefunden' ? 'Kurs nicht gefunden' : 'Gerade nicht erreichbar'}
          </h1>
          <p className="ks-gedaempft">
            {fehler === 'nicht_gefunden'
              ? <>Diesen Kurs gibt es nicht (mehr). <a href="/kursplan" className="ks-link">Zum Kursplan</a></>
              : 'Bitte versuche es in ein paar Minuten nochmal.'}
          </p>
        </div>
      </div>
    );
  }
  if (!kurs) return <div className="ks-wurzel"><Stil /><div className="ks-mitte"><p className="ks-gedaempft">Lädt …</p></div></div>;

  const wann = rhythmus(kurs.termine);

  return (
    <div className="ks-wurzel"><Stil />

      {/* ---------------------------------------------------------- Kopf */}
      <header className="ks-kopf">
        <div className="ks-diagonale" ref={parallaxRef} aria-hidden="true" />
        <div className="ks-band">
          <img src="/logo.png" alt="P1 Performance Center" className="ks-logo"
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div className="ks-kopf-inhalt">
            <p className="ks-augenbraue">{kurs.oberzeile}</p>
            <h1 className="ks-h1">{titelMitAkzent(kurs.titel)}</h1>
            {kurs.kurz && <p className="ks-untertitel">{kurs.kurz}</p>}
            <div className="ks-chips">
              {[kurs.alter, wann, kurs.dauer_min && `${kurs.dauer_min} Minuten`, 'Erste Stunde kostenlos']
                .filter(Boolean).map((t) => <span key={t} className="ks-chip">{t}</span>)}
            </div>
            <a href={kurs.funnel} className="ks-knopf ks-knopf-kopf">Kostenloses Probetraining</a>
          </div>
        </div>
      </header>

      <main className="ks-rumpf">
        {kurs.text_fehlt && (
          <p className="ks-warnung">
            Für diesen Kurs ist noch kein Text freigegeben. Die Seite zeigt nur,
            was im Kursplan steht — bitte erst veröffentlichen, wenn der Text steht.
          </p>
        )}

        {/* Bild — erscheint nur, wenn unter /kurs-bilder/<slug>.webp eines liegt. */}
        {bildDa && (
          <Auf className="ks-bildrahmen">
            <img src={`/kurs-bilder/${kurs.slug}.webp`} alt={kurs.titel}
                 className="ks-bild" onError={() => setBildDa(false)} />
          </Auf>
        )}

        {/* ---------------------------------------------- Das erwartet dich */}
        {kurs.erwartet.length > 0 && (
          <section className="ks-abschnitt">
            <Auf><h2 className="ks-h2">Das erwartet <span>Dich</span></h2></Auf>
            <ul className="ks-liste">
              {kurs.erwartet.map((z, i) => (
                <Auf als="li" key={z} verz={i * 70} className="ks-punkt">
                  <span className="ks-strich" aria-hidden="true" />{z}
                </Auf>
              ))}
            </ul>
          </section>
        )}

        {/* ---------------------------------------------------- Termine */}
        <section className="ks-abschnitt ks-abschnitt-panel">
          <Auf><h2 className="ks-h2">Nächste <span>Termine</span></h2></Auf>
          {kurs.termine_stehen_aus ? (
            <Auf><p className="ks-gedaempft">
              Für die nächsten Wochen sind noch keine Termine eingetragen.
              Schreib uns — wir sagen Dir, wann der Kurs wieder startet.
            </p></Auf>
          ) : (
            <div className="ks-termine">
              {kurs.termine.slice(0, 6).map((t, i) => {
                const z = terminText(t.start);
                const knapp = t.restplaetze <= 4;
                return (
                  <Auf key={t.termin_id} verz={i * 60} className="ks-termin">
                    <div className="ks-daybox">
                      <span className="ks-dow">{z.tag}</span>
                      <span className="ks-dnum">{z.tagNr}</span>
                      <span className="ks-mon">{z.monat}</span>
                    </div>
                    <div className="ks-termin-mitte">
                      <span className="ks-zeit">{z.zeit} Uhr</span>
                      <span className="ks-termin-dauer">{kurs.dauer_min} Minuten</span>
                    </div>
                    <span className={`ks-plaetze${knapp ? ' knapp' : ''}`}>
                      <b>{t.restplaetze}</b>{t.restplaetze === 1 ? 'Platz frei' : 'Plätze frei'}
                    </span>
                  </Auf>
                );
              })}
            </div>
          )}
        </section>

        {/* ------------------------------------------------- Mitbringen */}
        {kurs.mitbringen.length > 0 && (
          <section className="ks-abschnitt">
            <Auf><h2 className="ks-h2">Was Ihr <span>mitbringt</span></h2></Auf>
            <Auf verz={80}><p className="ks-fliess">{kurs.mitbringen.join(' · ')}</p></Auf>
            {kurs.eltern && <Auf verz={140}><p className="ks-gedaempft">{kurs.eltern}</p></Auf>}
          </section>
        )}

        {/* -------------------------------------------------- Anmeldung */}
        <section className="ks-abschnitt">
          <Auf className="ks-cta">
            <h2 className="ks-h2 ks-cta-titel">Probetraining <span>sichern</span></h2>
            <p className="ks-gedaempft" style={{ margin: '0 0 22px' }}>
              Kostenlos und unverbindlich. Termin in etwa einer Minute gewählt.
            </p>
            <a href={kurs.funnel} className="ks-knopf">Termin wählen</a>
          </Auf>
        </section>

        {/* -------------------------------------------------------- FAQ */}
        {kurs.faq.length > 0 && (
          <section className="ks-abschnitt ks-abschnitt-panel">
            <Auf><h2 className="ks-h2">Häufige <span>Fragen</span></h2></Auf>
            <div className="ks-faq">
              {kurs.faq.map((e, i) => (
                <Auf key={e.frage} verz={i * 60} className="ks-faq-block">
                  <p className="ks-frage">{e.frage}</p>
                  <p className="ks-antwort">{e.antwort}</p>
                </Auf>
              ))}
            </div>
          </section>
        )}

        <footer className="ks-fuss">
          P1 Performance Center Eutingen · <a href="/kursplan" className="ks-link">Gesamter Kursplan</a>
        </footer>
      </main>
    </div>
  );
}

/* Letztes Wort der Überschrift in Akzentfarbe — wie auf der Seminar-Seite
   ("Lerne von unserem TRAINERTEAM.") */
function titelMitAkzent(titel) {
  const woerter = titel.split(' ');
  if (woerter.length === 1) return <span className="akzent">{titel}</span>;
  return <>{woerter.slice(0, -1).join(' ')}<br /><span className="akzent">{woerter.at(-1)}.</span></>;
}

function Stil() {
  return (
    <style>{`
      .ks-wurzel{
        min-height:100vh; background:#0d0d10; color:#f2efe9;
        font-family:'Inter',system-ui,sans-serif; line-height:1.55;
        -webkit-font-smoothing:antialiased; overflow-x:hidden;
      }
      .ks-mitte{max-width:620px; margin:0 auto; padding:100px 20px; text-align:center}
      .ks-link{color:#ef7c23}
      .ks-gedaempft{color:#8b8b93; line-height:1.65}
      .ks-fliess{font-size:16px; line-height:1.65}

      /* ---- Kopf mit diagonalem Akzentband (wie Seminar-Seite) ---- */
      .ks-kopf{position:relative; overflow:hidden; border-bottom:1px solid rgba(242,239,233,.12)}
      .ks-diagonale{
        position:absolute; top:-40%; right:-12%; width:64%; height:190%;
        background:linear-gradient(135deg,transparent 45%,#ef7c23 45%,#ef7c23 52%,transparent 52%);
        opacity:.15; pointer-events:none; will-change:transform;
      }
      .ks-band{max-width:860px; margin:0 auto; padding:42px 20px 52px; position:relative}
      .ks-logo{height:52px; display:block; margin-bottom:36px}
      .ks-augenbraue{
        font-size:12px; letter-spacing:.24em; text-transform:uppercase;
        color:#ef7c23; font-weight:700; margin:0 0 16px;
        animation:ksAuf .6s ease both;
      }
      .ks-h1{
        font-family:'Anton',sans-serif; font-weight:400; text-transform:uppercase;
        font-size:clamp(40px,8.5vw,76px); line-height:.94; letter-spacing:.01em;
        margin:0 0 20px; animation:ksAuf .6s .08s ease both;
      }
      .ks-h1 .akzent{color:#ef7c23}
      .ks-untertitel{
        font-size:17px; line-height:1.6; color:rgba(242,239,233,.62);
        max-width:600px; margin:0 0 22px; animation:ksAuf .6s .16s ease both;
      }
      .ks-chips{display:flex; flex-wrap:wrap; gap:8px 10px; margin-bottom:28px; animation:ksAuf .6s .24s ease both}
      .ks-chip{
        border:1px solid rgba(242,239,233,.16); border-radius:100px;
        padding:7px 14px; font-size:13px; font-weight:500;
      }
      .ks-knopf{
        display:inline-block; background:#ef7c23; color:#fff; font-weight:700;
        font-size:15px; padding:15px 30px; border-radius:10px; text-decoration:none;
        transition:background .16s, transform .12s;
      }
      .ks-knopf:hover{background:#d3671a}
      .ks-knopf:active{transform:translateY(1px)}
      .ks-knopf-kopf{animation:ksAuf .6s .32s ease both}

      /* ---- Rumpf: vollbreite Abschnitte, abwechselnde Flaechen ---- */
      .ks-rumpf{padding-bottom:70px}
      .ks-abschnitt{max-width:860px; margin:0 auto; padding:54px 20px 10px}
      .ks-abschnitt-panel{
        max-width:none; background:#141419;
        border-top:1px solid rgba(242,239,233,.07);
        border-bottom:1px solid rgba(242,239,233,.07);
        margin-top:44px; padding:54px 0 60px;
      }
      .ks-abschnitt-panel > *{max-width:860px; margin-left:auto; margin-right:auto; padding-left:20px; padding-right:20px}
      .ks-h2{
        font-family:'Anton',sans-serif; font-weight:400; text-transform:uppercase;
        font-size:clamp(26px,4.5vw,38px); letter-spacing:.01em; margin:0 0 26px;
      }
      .ks-h2 span{color:#ef7c23}

      .ks-warnung{
        max-width:820px; margin:26px auto 0; padding:12px 16px; border-radius:10px;
        border:1px solid rgba(239,124,35,.5); color:#ef7c23; font-size:13px;
      }

      .ks-bildrahmen{max-width:860px; margin:44px auto 0; padding:0 20px}
      .ks-bild{width:100%; border-radius:16px; display:block}

      .ks-liste{list-style:none; padding:0; margin:0; display:grid; gap:14px}
      .ks-punkt{display:flex; gap:14px; align-items:flex-start; font-size:16px; line-height:1.55}
      .ks-strich{flex-shrink:0; width:22px; height:3px; background:#ef7c23; border-radius:2px; margin-top:10px}

      /* ---- Termine mit Daybox (wie Seminar-Karten) ---- */
      .ks-termine{display:grid; gap:12px}
      .ks-termin{
        display:flex; align-items:center; gap:18px;
        background:#1b1b21; border:1px solid rgba(242,239,233,.1);
        border-radius:14px; padding:16px 20px;
        transition:border-color .18s, transform .18s;
      }
      .ks-termin:hover{border-color:rgba(239,124,35,.55); transform:translateY(-2px)}
      .ks-daybox{
        text-align:center; border-right:1px solid rgba(242,239,233,.08);
        padding-right:16px; min-width:58px;
      }
      .ks-dow{display:block; font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:#ef7c23; font-weight:700}
      .ks-dnum{display:block; font-family:'Anton',sans-serif; font-size:28px; line-height:1.05}
      .ks-mon{display:block; font-size:11px; color:#8b8b93; text-transform:uppercase; letter-spacing:.1em}
      .ks-termin-mitte{flex:1; min-width:0}
      .ks-zeit{display:block; font-size:17px; font-weight:600}
      .ks-termin-dauer{display:block; font-size:12px; color:#8b8b93; margin-top:2px}
      .ks-plaetze{font-size:12px; color:#8b8b93; text-align:right}
      .ks-plaetze b{display:block; font-family:'Anton',sans-serif; font-size:22px; color:#f2efe9; line-height:1}
      .ks-plaetze.knapp b{color:#ef7c23}

      .ks-cta{
        background:#1b1b21; border:1px solid rgba(242,239,233,.1); border-radius:18px;
        padding:40px 28px; text-align:center; position:relative; overflow:hidden;
      }
      .ks-cta::before{
        content:""; position:absolute; top:-60%; right:-18%; width:55%; height:220%;
        background:linear-gradient(135deg,transparent 46%,#ef7c23 46%,#ef7c23 51%,transparent 51%);
        opacity:.1; pointer-events:none;
      }
      .ks-cta-titel{margin-bottom:8px}

      .ks-faq{display:grid; gap:22px}
      .ks-frage{font-weight:700; font-size:16px; margin:0 0 6px}
      .ks-antwort{margin:0; color:#8b8b93; line-height:1.65}

      .ks-fuss{
        max-width:860px; margin:60px auto 0; padding:24px 20px 0;
        border-top:1px solid rgba(242,239,233,.1); font-size:12px; color:#8b8b93;
      }
      .ks-fuss .ks-link{color:inherit}

      /* ---- Bewegung ---- */
      @keyframes ksAuf{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
      .ks-auf{opacity:0; transform:translateY(20px); transition:opacity .55s ease, transform .55s ease; will-change:opacity,transform}
      .ks-auf.sichtbar{opacity:1; transform:none}
      @media(prefers-reduced-motion:reduce){
        *{animation:none!important; transition:none!important}
        .ks-auf{opacity:1; transform:none}
      }
      @media(max-width:560px){
        .ks-termin{gap:12px; padding:14px 14px}
        .ks-daybox{min-width:50px; padding-right:12px}
      }
    `}</style>
  );
}
